import React, { Component } from 'react';
import { Hub, Auth } from 'aws-amplify';
import { graphql, withApollo } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import { compose, mapProps } from 'recompose';

import LoadingPage from 'components/common/Loader/Spinner/Spinner';
import UserAgreement from 'components/threepp/login/UserAgreement';
// import NoData from 'pages/NoData/NoData';
import { getUserByEmail, updateUserProfile, setCurrentUser, updateUserStatus, addUser } from 'graphql/operations';
import { initLocalCache } from 'graphql/resolvers';
import { clearAll as clearAllSelectorsCache } from 'selectors/clearSelectorsCache';
import { normalizeEmail } from 'utils/functions';
import Routes from 'utils/routes/appRoutes';
import { Languages } from 'utils/constants';
import PortalLanguageSelection from 'components/threepp/login/PortalLanguageSelection';
import { showErrorMessage } from 'components/common/Alert/Alert';
import { withTranslation } from 'react-i18next';
import i18n from '../i18n';
import Logout from './Logout/Logout';

export class AuthApp extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isInitializing: true,
      showUserAgreementModal: false,
      showPortalLanguageModal: false,
      portalSelectedLanguage: '',
      isAuthenticated: false,
      // userHasData: false,
      pathname: '',
      firstName: '',
      lastName: '',
      licenceAgreed: false,
      userEmail: ''
    };
  }

  componentDidMount() {
    Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'cognitoHostedUI':
          this.removeSessionIsAuthenticating();
          break;
        case 'signOut':
          this.setState({ isInitializing: false, isAuthenticated: false });
          break;
        case 'signIn_failure':
        case 'cognitoHostedUI_failure':
          console.log('Sign in failure', data);
          break;
        case 'customOAuthState': {
          let pathname;
          if (sessionStorage.getItem('redirectPath') != null) pathname = sessionStorage.getItem('redirectPath');
          else pathname = decodeURIComponent(data).replace(/_/g, '-');
          this.setState({ pathname });
          this.checkUser();
          sessionStorage.removeItem('redirectPath');
          break;
        }
        default:
          break;
      }
    });

    if (!window.location.pathname.includes('logout')) {
      if (window.location.pathname !== '/' && sessionStorage.getItem('redirectPath') == null) {
        sessionStorage.setItem('redirectPath', window.location.pathname);
      }
      this.initiateLogin();
    }
  }

  componentWillUnmount() {
    Hub.remove('auth');
  }

  getSessionIsAuthenticating = () => sessionStorage.getItem('isAuthenticating');

  setSessionIsAuthenticating = () => sessionStorage.setItem('isAuthenticating', 'true');

  removeSessionIsAuthenticating = () => sessionStorage.removeItem('isAuthenticating');

  initiateLogin = () => {
    Auth.currentUserInfo()
      .then(userInfo => {
        if (!userInfo) {
          this.setSessionIsAuthenticating();
          // Need to remove hyphens from pathname because Okta splits state on hyphens...
          const windowPathname = window.location.pathname;
          const noHyphenPathname = windowPathname.replace(/-/g, '_');
          Auth.federatedSignIn({ provider: 'Okta', customState: noHyphenPathname });
        } else if (userInfo) {
          this.removeSessionIsAuthenticating();
          this.checkUser();
        } else {
          // Some sign in issue must have occured, remove isAuthenticating
          this.removeSessionIsAuthenticating();
        }
      })
      .catch(e => {
        console.log(e);
      });
  };

  updateUserStates = async (email, user) => {
    const { firstName, lastName, status, position, language, licenceAgreed } = user;
    this.setState({
      firstName,
      lastName,
      position,
      licenceAgreed,
      portalSelectedLanguage: language
    });
    if (!language || !licenceAgreed) {
      this.setState({ showPortalLanguageModal: true, isInitializing: false });
    }
    const languageCode = Languages[language?.toUpperCase()];
    !!language && i18n.changeLanguage(languageCode);
    document.documentElement.setAttribute('lang', languageCode);
    if (!licenceAgreed) {
      this.setState({ showUserAgreementModal: true });
    } else if (status === 'PENDING') {
      await this.updateUser();
    } else {
      await this.setUser(email, firstName, lastName, position, language);
      this.setState({ isInitializing: false });
      if (licenceAgreed && language) {
        this.setState({ isAuthenticated: true });
      }
    }
  };

  checkUser = async () => {
    const { client, history } = this.props;
    const { isAuthenticated, pathname } = this.state;
    try {
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });

      const email = normalizeEmail(user.attributes.email);
      const firstName = user.attributes.given_name;
      const lastName = user.attributes.family_name;

      this.setState({ userEmail: email });

      const result = await client.query({ query: getUserByEmail, variables: { email } });

      if (!result.data.getUserByEmail) {
        // Federated user with no Engage data logged in
        const res = await client.mutate({ mutation: addUser, variables: { email, firstName, lastName } });
        this.updateUserStates(email, res.data.addUser);
      } else {
        this.updateUserStates(email, result.data.getUserByEmail);
      }
      history.push({ pathname });
    } catch (e) {
      // Clear Apollo cache
      client.clearStore();
      // Clear selectors cache
      clearAllSelectorsCache();
      if (isAuthenticated) {
        this.setState({ isAuthenticated: false }, history.push(Routes.logout.path));
      }
    }
  };

  updateUser = async () => {
    const { client, updateUserStatus: propsUpdateUserStatus } = this.props;
    const { showUserAgreementModal, userEmail, portalSelectedLanguage } = this.state;
    const email = userEmail;
    try {
      const { data } = await propsUpdateUserStatus({ variables: { email, status: 'CONFIRMED', licenceAgreed: true } });
      initLocalCache(client.cache);
      await this.setUser(email, data.updateUserStatus.firstName, data.updateUserStatus.lastName, data.updateUserStatus.position);
    } catch (err) {
      console.error('error updating status', err);
      this.setState({
        isInitializing: false
      });
    } finally {
      if (showUserAgreementModal) {
        this.setState({ isInitializing: false, showUserAgreementModal: false /* userHasData: true,*/ });
      } else {
        this.setState({ isInitializing: false });
      }
      if (portalSelectedLanguage) {
        this.setState({ isAuthenticated: true });
      }
    }
  };

  updatePortalLang = async language => {
    const { updateUserProfile: propsUpdateUserProfile, t } = this.props;
    const { userEmail: email, firstName, lastName, position, licenceAgreed } = this.state;
    try {
      const {
        data: { updateUserProfile: updatedUser }
      } = await propsUpdateUserProfile({
        variables: {
          email,
          firstName,
          lastName,
          country: 'US',
          position,
          language
        }
      });
      await this.setUser(updatedUser.email, updatedUser.firstName, updatedUser.lastName, updatedUser.position, updatedUser.language);
      i18n.changeLanguage(Languages[language?.toUpperCase()]);
      document.documentElement.setAttribute('lang', Languages[language?.toUpperCase()]);
      this.setState({ showPortalLanguageModal: false, portalSelectedLanguage: language });
      if (licenceAgreed) {
        this.setState({ isAuthenticated: true });
      }
    } catch {
      showErrorMessage(t('portalLanguageSelection.portalLanguageUpdateError'));
    }
  };

  setUser = async (email, firstName, lastName, position, language) => {
    const { client, setCurrentUser: propsSetCurrentUser } = this.props;
    initLocalCache(client.cache);
    await propsSetCurrentUser({ variables: { email, firstName, lastName, position, language } });
  };

  closeAgreementModal = () => {
    this.setState({ showUserAgreementModal: false });
    const { history } = this.props;
    history.push(Routes.logout.path);
  };

  render() {
    const { children } = this.props;
    const {
      isInitializing,
      isAuthenticated,
      /* userHasData,*/
      showUserAgreementModal,
      showPortalLanguageModal,
      portalSelectedLanguage
    } = this.state;
    let authContent = <span />;

    if (window.location.pathname.includes('logout')) {
      authContent = <Logout />;
    } else if (isInitializing) {
      authContent = <LoadingPage size="lg" expanded isFullWidth />;
    } else if (isAuthenticated) {
      authContent = children;
    }
    // else if (!userHasData) {
    //   authContent = <NoData />;
    // }

    return (
      <>
        <PortalLanguageSelection
          isOpen={showPortalLanguageModal}
          portalSelectedLanguage={portalSelectedLanguage}
          updatePortalLang={this.updatePortalLang}
        />
        <UserAgreement isOpen={!showPortalLanguageModal && showUserAgreementModal} toggle={this.closeAgreementModal} updateUser={this.updateUser} />
        {authContent}
      </>
    );
  }
}

const enhance = compose(
  withTranslation(),
  withApollo,
  withRouter,
  graphql(updateUserProfile, { name: 'updateUserProfile' }),
  graphql(updateUserStatus, { name: 'updateUserStatus' }),
  graphql(setCurrentUser, { name: 'setCurrentUser' }),
  mapProps(props => ({
    t: props.t,
    history: props.history,
    location: props.location,
    children: props.children,
    client: props.client,
    updateUserProfile: props.updateUserProfile,
    updateUserStatus: props.updateUserStatus,
    setCurrentUser: props.setCurrentUser
  }))
);

export default enhance(AuthApp);
